import { useId, type SelectHTMLAttributes } from 'react';
import { Icon } from './Icon';
import './Select.css';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string = string> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  /** MVP-264: required, and deliberately so. This component shipped with no way to name itself at
   * all, and four Settings selects announced as "combo box, Dark" with no field name. `Switch` and
   * `Checkbox` both take a `label`; a control that cannot be named is not an option here. Pass
   * MVP-453 corrected the sentence that used to end this comment — "every call site already
   * renders its own visible caption" was a claim, not a rule, and the invite form's role select had
   * been failing it since it shipped: a bare box between two captioned fields, choosing the
   * permissions of whoever redeems the link. See `showLabel`. */
  label: string;
  /**
   * MVP-453. Renders `label` as a real `<label htmlFor>` above the box, in `TextField`'s own
   * caption style, so the two sit in a row as one field and not as a field beside a mystery.
   *
   * A `<label>` element, not a visible `<span>` plus the `aria-label`: clicking the caption has to
   * focus the control the way it does for every text field in this app, and two names for one
   * control is how they drift apart.
   *
   * Opt-in rather than the default because a select inside a table column already has its caption
   * in the column header, and repeating it on every row is noise (`AdminUsersScreen`'s per-account
   * role picker is the case). The rule is that the word is on screen SOMEWHERE near the control —
   * this prop is for when nothing else is providing it.
   */
  showLabel?: boolean;
}

/** DESIGN.md §9.1 `Select`. A native `<select>` (real keyboard nav, real screen-reader support,
 * no reinvented affordance per operate.md) styled to the token set with a leading-appearance
 * chevron; DESIGN's row height/radius/border tokens keep it visually identical to `TextField`. */
export function Select<T extends string = string>({
  value,
  onChange,
  options,
  label,
  showLabel = false,
  className,
  disabled,
  id,
  ...rest
}: SelectProps<T>) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const box = (
    <div className={['select', disabled ? 'select--disabled' : '', showLabel ? '' : className].filter(Boolean).join(' ')}>
      {/* `aria-label` sits before the spread so a caller with a more specific name still wins.
          Dropped entirely when the caption is on screen: the `<label>` already names the control,
          and a second name is one that can drift from the visible one. */}
      <select
        id={selectId}
        className="select__input text-body"
        aria-label={showLabel ? undefined : label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.value as T)}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <Icon name="chevron-down" size={16} className="select__chevron" />
    </div>
  );

  if (!showLabel) return box;
  // `.field`/`.field__label` are `TextField`'s, deliberately: a row of filters is read as one row
  // only if every caption in it is the same size, colour and distance from its box.
  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <label htmlFor={selectId} className="field__label text-meta">
        {label}
      </label>
      {box}
    </div>
  );
}
